import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Clock, Trash2, Zap } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PRESETS = [
  { label: '30 min', minutes: 30 },
  { label: '1 hr', minutes: 60 },
  { label: '2 hr', minutes: 120 },
  { label: '4 hr', minutes: 240 },
  { label: 'Rest of night', minutes: 480 },
];

function authHeaders() {
  const token = localStorage.getItem('bar_owner_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function humanCountdown(iso) {
  const end = new Date(iso).getTime();
  const now = Date.now();
  const ms = end - now;
  if (ms <= 0) return null;
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function BarSpecialsPanel() {
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(60);
  const [customEnd, setCustomEnd] = useState('');
  const [mine, setMine] = useState([]);
  const [saving, setSaving] = useState(false);
  const [_, setTick] = useState(0);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/bar-owner/specials/mine`, { headers: authHeaders() });
      setMine(res.data?.specials || []);
    } catch (e) {
      // silent — bar-owner may not be logged in yet
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(() => setTick((t) => t + 1), 30000); // refresh countdowns every 30s
    return () => clearInterval(iv);
  }, []);

  const submit = async () => {
    if (!title.trim()) return toast.error('Give the special a title');
    setSaving(true);
    try {
      const body = { title: title.trim(), description: desc.trim() || null };
      if (customEnd) {
        body.custom_end_at = new Date(customEnd).toISOString();
      } else {
        body.duration_minutes = selectedPreset;
      }
      await axios.post(`${API}/bar-owner/specials/create`, body, { headers: authHeaders() });
      toast.success('Special posted!');
      setTitle('');
      setDesc('');
      setCustomEnd('');
      setSelectedPreset(60);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to post');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this special?')) return;
    try {
      await axios.delete(`${API}/bar-owner/specials/${id}`, { headers: authHeaders() });
      load();
    } catch (e) {
      toast.error('Could not delete');
    }
  };

  return (
    <Card data-testid="specials-panel" className="bg-zinc-900/70 border border-purple-500/40 p-5 space-y-5">
      <div className="flex items-center gap-2">
        <Zap className="h-5 w-5 text-purple-300" />
        <h3 className="font-black text-white tracking-widest text-sm">POST A LIVE SPECIAL</h3>
      </div>

      <div className="space-y-3">
        <Input
          data-testid="special-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. 2 for 1 Margaritas"
          className="bg-black/50 border-purple-500/40 text-white"
        />
        <Textarea
          data-testid="special-desc-input"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          placeholder="Optional details — any conditions, hashtags, etc."
          className="bg-black/50 border-purple-500/40 text-white text-sm"
          rows={2}
        />

        {/* Preset picks */}
        <div>
          <p className="text-xs text-zinc-400 mb-1">How long is it live?</p>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.minutes}
                data-testid={`preset-${p.minutes}`}
                onClick={() => { setSelectedPreset(p.minutes); setCustomEnd(''); }}
                className={`px-3 py-1 rounded-full text-xs font-mono border transition ${
                  !customEnd && selectedPreset === p.minutes
                    ? 'bg-purple-600 border-purple-400 text-white'
                    : 'bg-black/40 border-purple-500/40 text-purple-200 hover:border-purple-400'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom end time */}
        <div>
          <p className="text-xs text-zinc-400 mb-1">Or pick exact end time</p>
          <Input
            data-testid="special-custom-end-input"
            type="datetime-local"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            className="bg-black/50 border-purple-500/40 text-white"
          />
        </div>

        <Button
          data-testid="special-post-btn"
          onClick={submit}
          disabled={saving}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 font-bold"
        >
          {saving ? 'Posting…' : 'POST SPECIAL'}
        </Button>
      </div>

      {/* My specials list */}
      {mine.length > 0 && (
        <div className="pt-3 border-t border-zinc-700 space-y-2">
          <p className="text-xs font-mono text-purple-300/80 tracking-widest">◆ YOUR SPECIALS</p>
          {mine.map((s) => {
            const active = s.status === 'active';
            const cd = active ? humanCountdown(s.active_end_at) : null;
            return (
              <div
                key={s.id}
                data-testid={`my-special-${s.id}`}
                className={`rounded border p-3 flex items-start justify-between gap-3 ${
                  active ? 'border-emerald-500/40 bg-emerald-900/10' : 'border-zinc-600 bg-zinc-800/40'
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-bold text-white text-sm">{s.title}</p>
                    <Badge className={active ? 'bg-emerald-500' : 'bg-zinc-600'}>
                      {active ? 'ACTIVE' : 'ENDED'}
                    </Badge>
                  </div>
                  {s.description && <p className="text-xs text-zinc-300">{s.description}</p>}
                  <p className="text-[10px] text-zinc-400 font-mono mt-1 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {active ? `Ends in ${cd}` : `Ended · hides at ${new Date(s.hide_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                  </p>
                </div>
                <button
                  data-testid={`delete-special-${s.id}`}
                  onClick={() => remove(s.id)}
                  className="text-red-300 hover:text-red-100"
                  aria-label="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
